import { useEffect, useState } from 'react';
import { plural, t } from '@lingui/macro';

import { HeaderMismatch } from '.';

/**
 * Resolves which columns are wrong in an already-rejected upload, so the error
 * message can name them instead of leaving the user to diff two header rows by
 * eye.
 *
 * Deliberately scoped to the step components: the mismatch is only ever needed
 * to render one message, so it does not belong in the modal state that decides
 * whether the user may continue. Only runs while `enabled`, which the caller
 * ties to the header error, so a valid upload never re-reads the file.
 */
export const useTemplateMismatch = (
  file: File | null,
  enabled: boolean,
  find: (file: File) => Promise<HeaderMismatch>,
): HeaderMismatch | null => {
  const [mismatch, setMismatch] = useState<HeaderMismatch | null>(null);

  useEffect(() => {
    if (!file || !enabled) {
      setMismatch(null);
      return undefined;
    }

    let cancelled = false;
    find(file)
      .then((result) => {
        if (!cancelled) setMismatch(result);
      })
      .catch(() => {
        // The message falls back to its generic form; the upload is already
        // rejected either way.
        if (!cancelled) setMismatch(null);
      });

    return () => {
      cancelled = true;
    };
  }, [file, enabled, find]);

  return mismatch;
};

/** "Odometer" / "Odometer and Engine Hours" / "A, B and C". */
export const listColumns = (columns: string[]): string =>
  columns.length <= 1
    ? columns.join('')
    : `${columns.slice(0, -1).join(', ')} and ${columns[columns.length - 1]}`;

/**
 * The message shown when an upload is rejected on its headers. Names the
 * offending columns when they are known, so the user is not left comparing two
 * header rows by hand, and falls back to the generic wording otherwise.
 *
 * Each half is built as its own pluralized clause and then composed, because a
 * mismatch can name several columns on either side (a trimmed template is
 * missing both Odometer and Engine Hours) and the two counts differ
 * independently.
 */
export const buildHeaderErrorMessage = (
  mismatch: HeaderMismatch | null,
): string => {
  const generic = t`This file's columns do not match the template. Download the latest CSV template and try again.`;
  if (!mismatch) return generic;

  const missingCount = mismatch.missing.length;
  const unexpectedCount = mismatch.unexpected.length;
  const missingList = listColumns(mismatch.missing);
  const unexpectedList = listColumns(mismatch.unexpected);

  const missingClause = plural(missingCount, {
    one: `the ${missingList} column is missing`,
    other: `the ${missingList} columns are missing`,
  });
  const unexpectedClause = plural(unexpectedCount, {
    one: `the ${unexpectedList} column does not belong`,
    other: `the ${unexpectedList} columns do not belong`,
  });

  if (missingCount && unexpectedCount) {
    return t`This file's columns do not match the template: ${missingClause} and ${unexpectedClause}. Download the latest CSV template and try again.`;
  }
  if (missingCount) {
    return t`This file's columns do not match the template: ${missingClause}. Download the latest CSV template and try again.`;
  }
  if (unexpectedCount) {
    return t`This file's columns do not match the template: ${unexpectedClause}. Download the latest CSV template and try again.`;
  }
  return generic;
};
